import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getReportsCentre, GetReportsCentreOutputType, GetReportsCentreInputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, FileText, BarChart3, Search, Shield, Users, BookOpen, CalendarDays, Star, MessageSquare, ClipboardList, Award, FileCheck } from 'lucide-react';
import { isManagerPlus, isOwnerOrAdmin } from '@/lib/roles';

type ReportType = GetReportsCentreInputType['report'];
type ReportData = GetReportsCentreOutputType;

const REPORTS: { id: ReportType; label: string; icon: React.FC<{ className?: string }>; description: string; minRole: 'manager' | 'admin' }[] = [
  { id: 'staff-roster', label: 'Staff Roster', icon: Users, description: 'Active staff summary', minRole: 'manager' },
  { id: 'training-completion', label: 'Training Completion', icon: BookOpen, description: 'Module completion by staff', minRole: 'manager' },
  { id: 'compliance-status', label: 'Compliance Status', icon: Shield, description: 'Compliance & certificate status', minRole: 'manager' },
  { id: 'onboarding-progress', label: 'Onboarding Progress', icon: ClipboardList, description: 'New starter journeys', minRole: 'manager' },
  { id: 'leave-requests', label: 'Leave & Availability', icon: CalendarDays, description: 'Leave request history', minRole: 'manager' },
  { id: 'policy-acknowledgements', label: 'Policy Acknowledgements', icon: FileCheck, description: 'Acknowledgement gaps', minRole: 'manager' },
  { id: 'recognition', label: 'Recognition & Rewards', icon: Star, description: 'Peer recognition activity', minRole: 'manager' },
  { id: 'suggestions', label: 'Feedback & Ideas', icon: MessageSquare, description: 'Staff suggestions & feedback', minRole: 'manager' },
  { id: 'audit-log', label: 'Audit Log', icon: FileText, description: 'System audit trail', minRole: 'admin' },
];

export default function ReportsCentre() {
  const { user } = useAuth();
  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const canAccess = isManagerPlus(user?.role);
  const isAdmin = isOwnerOrAdmin(user?.role);

  const runReport = async (report: ReportType) => {
    setSelectedReport(report);
    setLoading(true);
    setData(null);
    try {
      const result = await getReportsCentre({
        report,
        departmentFilter: deptFilter || undefined,
        statusFilter: statusFilter || undefined,
      });
      setData(result);
    } catch (e: any) {
      toast.error(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!data?.csvData) return;
    const blob = new Blob([data.csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.title.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  if (!canAccess) {
    return (
      <div className="px-4 md:px-8 py-12 text-center">
        <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
        <h1 className="text-lg font-semibold text-foreground">Access Restricted</h1>
        <p className="text-sm text-muted-foreground mt-1">Reports are available to Managers, Admins, and Owners.</p>
      </div>
    );
  }

  const availableReports = REPORTS.filter(r => r.minRole === 'manager' || isAdmin);
  const filteredRows = data?.rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.col1.toLowerCase().includes(q) || r.col2.toLowerCase().includes(q) || r.col3.toLowerCase().includes(q);
  }) || [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Reports & Export Centre
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Role-aware reports with CSV export</p>
        </div>
      </div>

      {!selectedReport ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {availableReports.map(r => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                onClick={() => runReport(r.id)}
                className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/50 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{r.label}</p>
                    {r.minRole === 'admin' && <Badge variant="secondary" className="text-xs mt-0.5">Admin Only</Badge>}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{r.description}</p>
              </button>
            );
          })}
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setSelectedReport(null); setData(null); setSearch(''); }}>
                ← Back
              </Button>
              <h2 className="text-lg font-semibold text-foreground">{data?.title || 'Loading...'}</h2>
              {data && <Badge variant="secondary">{data.totalCount} records</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Select value={deptFilter || 'all'} onValueChange={v => { setDeptFilter(v === 'all' ? '' : v); if (selectedReport) runReport(selectedReport); }}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  <SelectItem value="Front of House">Front of House</SelectItem>
                  <SelectItem value="Back of House">Back of House</SelectItem>
                  <SelectItem value="Kitchen">Kitchen</SelectItem>
                  <SelectItem value="Management">Management</SelectItem>
                </SelectContent>
              </Select>
              {data && (
                <Button size="sm" variant="outline" onClick={downloadCsv}>
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                </Button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : data ? (
            <>
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search results..." className="pl-9 h-9" />
                </div>
              </div>
              <div className="border border-border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      {data.columns.map(c => (
                        <th key={c} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredRows.length === 0 ? (
                      <tr><td colSpan={data.columns.length} className="text-center py-8 text-muted-foreground text-sm">No data found</td></tr>
                    ) : filteredRows.slice(0, 100).map(r => (
                      <tr key={r.id} className="hover:bg-muted/30">
                        <td className="px-3 py-2 text-foreground whitespace-nowrap">{r.col1}</td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.col2}</td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.col3}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            r.col4 === 'Overdue' || r.col4 === 'Expired' ? 'bg-destructive/10 text-destructive' :
                            r.col4 === 'Active' || r.col4 === 'Valid' || r.col4 === 'Yes' || r.col4 === 'Approved' ? 'bg-primary/10 text-primary' :
                            'bg-muted text-muted-foreground'
                          }`}>{r.col4}</span>
                        </td>
                        {r.col5 && <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.col5}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredRows.length > 100 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">Showing 100 of {filteredRows.length} — download CSV for full data</p>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
